import api from './api';
import { Product, ProductPrice } from './productService';

export type CartStatus = 'active' | 'abandoned' | 'converted';

export interface CartItemProduct {
    id: number;
    title: string;
    type: string;
    thumbnailUrl: string | null;
    shortDescription: string | null;
}

export interface CartItemPrice {
    id: number;
    priceType: string;
    amount: string;
    currency: string;
    interval: string | null;
    intervalCount: number | null;
    formattedPrice: string;
}

export interface CartItem {
    id: number;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    discount: string | null;
    couponCode: string | null;
    product: CartItemProduct | null;
    price: CartItemPrice | null;
}

export interface Cart {
    id: number;
    uuid: string;
    status: CartStatus;
    itemCount: number;
    items: CartItem[];
    expiresAt: string;
    createdAt: string;
}

export interface CartTotals {
    subtotal: number;
    discount: number;
    tax: number;
    total: number;
    currency: string;
    items: number;
}

export interface CartResponse {
    cart: Cart;
    totals: CartTotals;
}

export interface CartValidation {
    valid: boolean;
    errors: string[];
    totals: CartTotals;
}

export interface AddToCartData {
    productId: number;
    priceId: number;
    quantity?: number;
}

class CartService {
    private sessionId: string | null = null;

    constructor() {
        // Get or create session ID for guest carts
        this.sessionId = localStorage.getItem('cart_session_id');
        if (!this.sessionId) {
            this.sessionId = this.generateSessionId();
            localStorage.setItem('cart_session_id', this.sessionId);
        }
    }

    private generateSessionId(): string {
        return 'sess_' + Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
    }

    private getHeaders(): Record<string, string> {
        const headers: Record<string, string> = {};
        if (this.sessionId) {
            headers['X-Session-Id'] = this.sessionId;
        }
        return headers;
    }

    /**
     * Get current cart
     */
    async getCart(): Promise<CartResponse> {
        const response = await api.get<CartResponse>('/cart', {
            headers: this.getHeaders(),
        });
        return response.data;
    }

    /**
     * Get cart by UUID (for guest carts)
     */
    async getCartByUuid(uuid: string): Promise<CartResponse> {
        const response = await api.get<CartResponse>(`/cart/uuid/${uuid}`);
        return response.data;
    }

    /**
     * Add item to cart
     */
    async addItem(data: AddToCartData): Promise<CartResponse & { success: boolean; item: CartItem }> {
        const response = await api.post<CartResponse & { success: boolean; item: CartItem }>(
            '/cart/items',
            data,
            { headers: this.getHeaders() }
        );
        return response.data;
    }

    /**
     * Update item quantity
     */
    async updateItemQuantity(itemId: number, quantity: number): Promise<CartResponse & { success: boolean; item: CartItem }> {
        const response = await api.patch<CartResponse & { success: boolean; item: CartItem }>(
            `/cart/items/${itemId}`,
            { quantity },
            { headers: this.getHeaders() }
        );
        return response.data;
    }

    /**
     * Remove item from cart
     */
    async removeItem(itemId: number): Promise<CartResponse & { success: boolean }> {
        const response = await api.delete<CartResponse & { success: boolean }>(
            `/cart/items/${itemId}`,
            { headers: this.getHeaders() }
        );
        return response.data;
    }

    /**
     * Clear all items from cart
     */
    async clearCart(): Promise<CartResponse & { success: boolean }> {
        const response = await api.post<CartResponse & { success: boolean }>(
            '/cart/clear',
            {},
            { headers: this.getHeaders() }
        );
        return response.data;
    }

    /**
     * Validate cart for checkout
     */
    async validateCart(): Promise<CartValidation> {
        const response = await api.get<CartValidation>('/cart/validate', {
            headers: this.getHeaders(),
        });
        return response.data;
    }

    /**
     * Add product with default price to cart
     */
    async addProductToCart(product: Product): Promise<CartResponse & { success: boolean; item: CartItem }> {
        const defaultPrice = product.prices?.find(p => p.isDefault && p.isActive)
            || product.prices?.find(p => p.isActive);

        if (!defaultPrice) {
            throw new Error('Kein gültiger Preis für dieses Produkt gefunden');
        }

        return this.addItem({
            productId: product.id,
            priceId: defaultPrice.id,
            quantity: 1,
        });
    }

    /**
     * Check if product is in cart
     */
    isProductInCart(cart: Cart, productId: number): boolean {
        return cart.items.some(item => item.product?.id === productId);
    }

    /**
     * Get cart item count
     */
    getItemCount(cart: Cart): number {
        return cart.items.reduce((sum, item) => sum + item.quantity, 0);
    }

    /**
     * Format currency amount
     */
    formatAmount(amount: number, currency: string = 'EUR'): string {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency,
        }).format(amount);
    }

    /**
     * Check if cart has subscriptions
     */
    hasSubscriptions(cart: Cart): boolean {
        return cart.items.some(item => item.price?.priceType === 'subscription');
    }

    /**
     * Check if cart has only free items
     */
    isCartFree(cart: Cart): boolean {
        return cart.items.every(item =>
            item.price?.priceType === 'free' || parseFloat(item.price?.amount || '0') === 0
        );
    }

    /**
     * Get session ID (for debugging)
     */
    getSessionId(): string | null {
        return this.sessionId;
    }

    /**
     * Clear session (on logout)
     */
    clearSession(): void {
        localStorage.removeItem('cart_session_id');
        this.sessionId = null;
    }
}

export const cartService = new CartService();
export default cartService;
